/**
 * The text-level rules of a styled post: what a highlight covers, where a Kashida
 * goes, which words may wear Mursala — and what happens to all of it when the
 * words underneath change.
 *
 * Everything here is a pure function over a `StyledPost` and its text, so each
 * rule is a test rather than a thing to try on a phone: the toolbar in the
 * composer calls these and keeps whatever comes back, and the card in the feed
 * only ever reads.
 *
 * Two of them are the Arabic typography the feature exists for:
 *
 *  - **Kashida** elongates a word by inserting the Tatweel character `\u0640`
 *    *before the last letter*, which is where a scribe would stretch a word: the
 *    connecting stroke grows between the second-to-last letter and the last one,
 *    and no letter is ever broken. Marks (harakat) stay where they were, which is
 *    why the insertion point is found by walking back over them.
 *  - **Mursala** is the calligraphic elongation the Thmanyah faces carry as an
 *    OpenType stylistic set. A card may not use it everywhere — two adjacent words
 *    wearing it is shouting — so the rules are: a word either has it or does not,
 *    applying it to a word takes it off the words on either side of it, and no
 *    sentence wears more than two.
 */
import {
  type StyledPost,
  STYLED_POST_MAX_RANGES,
  STYLED_POST_MAX_SIZE,
  STYLED_POST_MIN_SIZE,
  styledPostValue,
} from '#/state/postStyle/types'

/** The Tatweel — the character a Kashida is drawn with. */
export const KASHIDA = '\u0640'

/** A letter, or a Kashida already standing in for one. */
const LETTER_RE = /[\p{L}\u0640]/u

/** A combining mark — a harakat, which belongs to the letter before it. */
const MARK_RE = /\p{M}/u

/** Sentence terminators, in Arabic and Latin, and the line break. */
const SENTENCE_END_RE = /[.!?؟۔\u06D4\n]/u

export type TextRange = {start: number; end: number}

/**
 * The word around an offset, as a range — what a toolbar action applies to when
 * the author has a cursor rather than a selected word.
 *
 * Words are runs of letters and marks, so an Arabic word with harakat is one
 * word, and a word with a hyphen in it is two. That is the granularity both
 * Kashida and Mursala are defined at.
 */
export function wordAt(text: string, offset: number): TextRange | null {
  if (!text) return null
  const at = Math.max(0, Math.min(offset, text.length - 1))
  const isWordChar = (index: number) => {
    const char = text[index]
    return !!char && (LETTER_RE.test(char) || MARK_RE.test(char))
  }
  if (!isWordChar(at) && !isWordChar(at - 1)) return null
  let start = at
  while (start > 0 && isWordChar(start - 1)) start--
  let end = at
  while (end < text.length && isWordChar(end)) end++
  if (start === end) return null
  return {start, end}
}

/** Every word of a stretch of text, in order. */
export function wordsIn(text: string, range?: TextRange): TextRange[] {
  const from = range ? range.start : 0
  const to = range ? range.end : text.length
  const words: TextRange[] = []
  let index = from
  while (index < to) {
    if (LETTER_RE.test(text[index] ?? '')) {
      const word = wordAt(text, index)
      if (word && word.end <= to) {
        words.push(word)
        index = word.end
        continue
      }
    }
    index++
  }
  return words
}

/**
 * The sentence around an offset.
 *
 * Read by this file alone, for the Mursala limit: *no more than two per
 * sentence* is a rule about a sentence, and a sentence is a run of words between
 * terminators.
 */
export function sentenceAt(text: string, offset: number): TextRange {
  let start = 0
  for (let i = Math.min(offset, text.length - 1); i > 0; i--) {
    if (SENTENCE_END_RE.test(text[i - 1] ?? '')) {
      start = i
      break
    }
  }
  let end = text.length
  for (let i = Math.max(offset, 0); i < text.length; i++) {
    if (SENTENCE_END_RE.test(text[i] ?? '')) {
      end = i
      break
    }
  }
  return {start, end}
}

/** Whether two ranges touch at all. */
export function rangesOverlap(a: TextRange, b: TextRange): boolean {
  return a.start < b.end && b.start < a.end
}

/** Whether two ranges meet or overlap — the test for *adjacent words* next door. */
export function rangesTouch(a: TextRange, b: TextRange): boolean {
  return a.start <= b.end && b.start <= a.end
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max))
}

/** Ranges that still mean something against a text of this length. */
export function clampRanges<T extends TextRange>(
  ranges: readonly T[],
  length: number,
): T[] {
  return ranges
    .map(range => ({
      ...range,
      start: clamp(range.start, 0, length),
      end: clamp(range.end, 0, length),
    }))
    .filter(range => range.end > range.start)
}

/**
 * Where ranges land after the text under them is edited.
 *
 * The edit is described the way a person would describe it — the words before it
 * are the same, the words after it are the same, and something changed in the
 * middle — and every range is placed by which side of the change it was on:
 * entirely before it keeps its offsets, entirely after it moves by the change's
 * length, and one that straddles it grows or shrinks to cover what is left of it.
 *
 * This is what a highlight does while the author keeps typing: append a word at
 * the end and the highlight stays on its own word; delete half of a highlighted
 * phrase and the highlight shrinks with it; empty it and the highlight is gone
 * rather than sitting on the next words.
 */
export function reconcileRanges<T extends TextRange>(
  oldText: string,
  newText: string,
  ranges: readonly T[],
): T[] {
  if (oldText === newText) return clampRanges(ranges, newText.length)

  let prefix = 0
  while (
    prefix < oldText.length &&
    prefix < newText.length &&
    oldText[prefix] === newText[prefix]
  ) {
    prefix++
  }

  let suffix = 0
  while (
    suffix < oldText.length - prefix &&
    suffix < newText.length - prefix &&
    oldText[oldText.length - 1 - suffix] ===
      newText[newText.length - 1 - suffix]
  ) {
    suffix++
  }

  const delta = newText.length - oldText.length
  /*
   * Where the change was in the old text: from `prefix` to `unchangedTailFrom`.
   * Everything before it kept its offsets, everything after it moved by `delta`.
   */
  const changedFrom = prefix
  const changedToOld = oldText.length - suffix

  const moved = ranges.map(range => {
    // Entirely before the change: nothing about it moved.
    if (range.end <= changedFrom) return range
    // Entirely after it: it slid by exactly the length of the change.
    if (range.start >= changedToOld) {
      return {...range, start: range.start + delta, end: range.end + delta}
    }
    /*
     * It met the change somewhere in the middle, and the two cases that matter
     * are answered differently on purpose.
     *
     * A range that *started* at or inside the change began after it, so it slid:
     * type in front of a highlighted word and the highlight stays on the word.
     * A range the change landed *inside* of keeps its beginning and takes the
     * change at its end, so it grows as words are typed into it and shrinks as
     * they are deleted out of it — and when it is shrunk to nothing, it is gone
     * rather than sitting on the words that followed.
     */
    if (range.start >= changedFrom) {
      return {
        ...range,
        start: Math.max(changedFrom, range.start + delta),
        end: range.end + delta,
      }
    }
    return {
      ...range,
      start: Math.min(range.start, changedFrom),
      end: range.end + delta,
    }
  })
  return clampRanges(moved as T[], newText.length)
}

/**
 * A style, re-anchored to the text it is now being drawn with.
 *
 * Called from both ends of a card's life: on the way *out*, against the text the
 * record will really carry (the app trims it), so what is written down is already
 * exact; and on the way *in*, against the text the record really carries, so a
 * mark whose snapshot disagrees — because the author edited the post on another
 * client, which is allowed — is moved onto the words that are there instead of
 * being drawn on whatever happens to be at those offsets.
 */
export function resolveStyledPost(style: StyledPost, text: string): StyledPost {
  const highlights = clampRanges(style.highlights ?? [], text.length)
  const movedHighlights =
    style.text === text
      ? highlights
      : clampRanges(reconcileRanges(style.text, text, highlights), text.length)
  const mursalas = clampRanges(style.mursalas ?? [], text.length)
  const movedMursalas =
    style.text === text
      ? mursalas
      : clampRanges(reconcileRanges(style.text, text, mursalas), text.length)

  return {
    ...style,
    text,
    highlights: movedHighlights.length ? movedHighlights : undefined,
    mursalas: movedMursalas.length ? movedMursalas : undefined,
    /*
     * A size is only written down when the author chose one: the default is the
     * card's business, not the mark's, so leaving it out keeps the mark small and
     * leaves the default free to change in a later build.
     */
    size:
      style.size === undefined
        ? undefined
        : clamp(style.size, STYLED_POST_MIN_SIZE, STYLED_POST_MAX_SIZE),
  }
}

/**
 * The mark a post is published with — or `null` when there is nothing to say.
 *
 * Nothing to say is a card with no words: an empty background is not a styled
 * post, it is an empty post, and the composer will not publish one. Everything
 * else travels, re-anchored to the text the record is about to carry so the
 * snapshot and the ranges agree before they leave the device.
 */
export function styledPostMark(
  style: StyledPost,
  publishedText: string,
): Record<string, unknown> | null {
  if (!publishedText.trim()) return null
  return styledPostValue(resolveStyledPost(style, publishedText))
}

/**
 * The words inside a range, with the whitespace at either end taken off.
 *
 * A selection is a range, and a range is *positions*, not words: a double-tap on a
 * phone and a double-click in a browser both tend to take the space after the word
 * with it, and a selection dragged by hand often takes the one before it as well.
 * Every control that acts on words — a Kashida, a Mursala, a highlight — then has to
 * answer the same question, so it is answered once, here, and all three agree: a
 * highlight never paints a coloured block in front of its first word, a Kashida is
 * never refused because of the space that came with the word, and a Mursala never
 * stretches the space itself.
 *
 * `null` when the range holds nothing but whitespace — there is no word to act on,
 * which is a real answer rather than an error, and it is what the toolbar says so.
 */
export function trimToWords(text: string, range: TextRange): TextRange | null {
  let start = clamp(range.start, 0, text.length)
  let end = clamp(range.end, 0, text.length)
  while (start < end && /\s/.test(text[start] ?? '')) start++
  while (end > start && /\s/.test(text[end - 1] ?? '')) end--
  return end > start ? {start, end} : null
}

/**
 * Stretching a word with a Kashida.
 *
 * The Tatweel goes in *before the last letter*, and pressing again stretches the
 * same stroke further: the search walks back over the diacritics that belong to
 * that letter and over any Tatweel already inserted, so a second press adds to
 * the same join rather than starting a new one somewhere else. A range with no
 * letter in it — a space, a punctuation mark — is left alone, and the space a
 * selection swept up beside a word is not part of it: the stretch is asked of a
 * *word*, which is what the words are found first for.
 */
export function insertKashida(
  text: string,
  range: TextRange,
): {text: string; range: TextRange} | null {
  const words = trimToWords(text, range)
  if (!words) return null
  const from = words.start
  const to = words.end

  let last = to - 1
  while (last >= from && !LETTER_RE.test(text[last] ?? '')) {
    if (!MARK_RE.test(text[last] ?? '') && text[last] !== KASHIDA) return null
    last--
  }
  if (last < from) return null

  // Walk back to the letter this one belongs with, past marks and past any
  // Tatweel already standing between it and its neighbour.
  let insertAt = last
  while (
    insertAt > from &&
    (MARK_RE.test(text[insertAt - 1] ?? '') || text[insertAt - 1] === KASHIDA)
  ) {
    insertAt--
    if (text[insertAt] === KASHIDA) {
      while (insertAt > from && text[insertAt - 1] === KASHIDA) insertAt--
      break
    }
  }

  return {
    text: text.slice(0, insertAt) + KASHIDA + text.slice(insertAt),
    range: {start: Math.min(from, insertAt), end: to + 1},
  }
}

/**
 * Taking every Kashida back off a stretch of words.
 *
 * The Tatweel is a character like any other once it is in the text, so undoing a
 * Kashida is removing it — and the ranges drawn over the words are re-anchored
 * afterwards by `resolveStyledPost`, exactly as they are when the author types.
 * Written here, beside `insertKashida`, because the two are one idea: a stretched
 * word is a word with a character in it, not a property of the design.
 */
export function stripKashida(text: string): string {
  return text.replaceAll(KASHIDA, '')
}

/**
 * Painting a highlight behind a stretch of words.
 *
 * Pressing on words that are already highlighted takes it off — one control, one
 * meaning — and painting across two highlights joins them into one rather than
 * stacking two colours behind the same letters.
 */
export function toggleHighlight(
  style: StyledPost,
  range: TextRange,
  color: string,
): StyledPost {
  const existing = style.highlights ?? []
  const same = existing.find(
    entry => entry.start === range.start && entry.end === range.end,
  )
  if (same && same.color === color) {
    return {
      ...style,
      highlights: existing.filter(entry => entry !== same),
    }
  }

  const touched = existing.filter(entry => rangesOverlap(entry, range))
  const merged = touched.length
    ? [
        {
          start: Math.min(range.start, ...touched.map(entry => entry.start)),
          end: Math.max(range.end, ...touched.map(entry => entry.end)),
          color,
        },
      ]
    : [{start: range.start, end: range.end, color}]

  const kept = existing.filter(entry => !touched.includes(entry))
  const highlights = [...kept, ...merged]
    .sort((a, b) => a.start - b.start)
    .slice(0, STYLED_POST_MAX_RANGES)

  return {...style, highlights, highlightColor: color}
}

/**
 * Putting the Mursala elongation on a word, by the rules it is allowed.
 *
 * One word at a time, never two side by side: applying it to a word first takes
 * it off the word before and the word after, which is exactly what *applying it
 * to a neighbour* means when the neighbour was the one wearing it. Within a
 * sentence, never more than two: a third pushing in takes the earliest off, so
 * the effect stays what it is for — one or two words made longer than the rest of
 * the line.
 */
export function toggleMursala(
  style: StyledPost,
  text: string,
  range: TextRange,
): StyledPost {
  /*
   * A Mursala is drawn *on a word*, so the space a selection swept up beside it is
   * not part of the mark — the same rule a Kashida follows, stated once in
   * `trimToWords`.
   */
  const mark = trimToWords(text, range)
  if (!mark) return style

  const existing = clampRanges(style.mursalas ?? [], text.length)
  const already = existing.find(entry => rangesOverlap(entry, mark))
  if (already) {
    return {...style, mursalas: existing.filter(entry => entry !== already)}
  }

  const sentence = sentenceAt(text, mark.start)
  const words = wordsIn(text, sentence)
  const index = words.findIndex(word => rangesOverlap(word, mark))
  const neighbours = [words[index - 1], words[index + 1]].filter(
    (word): word is TextRange => !!word,
  )

  /*
   * The word before and the word after, and nothing else: a Mursala in *another*
   * sentence is not a neighbour and is left exactly where it is — the limit is a
   * limit on a sentence, not on a card.
   */
  let next = existing.filter(
    entry =>
      !rangesOverlap(entry, mark) &&
      !neighbours.some(neighbour => rangesTouch(entry, neighbour)),
  )

  const inSentence = next
    .filter(entry => entry.start >= sentence.start && entry.end <= sentence.end)
    .sort((a, b) => a.start - b.start)
  if (inSentence.length >= 2) {
    const oldest = inSentence[0]
    next = next.filter(entry => entry !== oldest)
  }

  return {
    ...style,
    mursalas: [...next, {start: mark.start, end: mark.end}]
      .sort((a, b) => a.start - b.start)
      .slice(0, STYLED_POST_MAX_RANGES),
  }
}

/** One stretch of the card's words, and what is drawn behind or about it. */
export type StyledPostSegment = {
  text: string
  highlight?: string
  mursala?: boolean
  /**
   * In the author's current selection, which is an *editor* thing: a reader's copy is
   * never drawn with one, and it is not part of the mark.
   */
  selected?: boolean
}

/**
 * The card's words, cut into the stretches that are drawn differently.
 *
 * One pass over the ranges rather than one `Text` per word: every offset that
 * starts or ends a highlight or a Mursala is a boundary, and between two
 * boundaries the words are drawn the same, so a card with two highlights and one
 * Mursala lays out as a handful of segments instead of a nested tree the width of
 * its text. This is what makes the highlight look like a printer's block — the
 * background is the segment's, so it follows the text and steps unevenly across
 * a wrapped line, exactly as the guidelines ask.
 *
 * `selection` is the author's own selection, and it is a *boundary like any other*: the
 * editor can therefore draw the selection on the design's own words, at the design's own
 * size, instead of leaving the input to paint a block of its own over them — which is
 * what a browser does to invisible text, and what a selection over a long post looked
 * like: a column of the height of the composition.
 */
export function styledPostSegments(
  /*
   * Only the two sets of ranges, not a whole card: the invisible input under a design
   * asks for the same cut of the same words (`TextInput`), and it holds no card of its
   * own — the words on the screen are the design's.
   */
  style: Pick<StyledPost, 'highlights' | 'mursalas'>,
  text: string,
  selection?: {start: number; end: number} | null,
): StyledPostSegment[] {
  if (!text) return []
  const highlights = clampRanges(style.highlights ?? [], text.length)
  const mursalas = clampRanges(style.mursalas ?? [], text.length)
  /* A selection written by an input that has since been edited is read back onto the
     words that are there, the way every other range is. */
  const mark = clampRanges(
    selection && selection.end > selection.start ? [selection] : [],
    text.length,
  )[0]
  if (!highlights.length && !mursalas.length && !mark) return [{text}]

  const boundaries = new Set<number>([0, text.length])
  for (const range of [...highlights, ...mursalas, ...(mark ? [mark] : [])]) {
    boundaries.add(range.start)
    boundaries.add(range.end)
  }
  const stops = [...boundaries].sort((a, b) => a - b)

  const segments: StyledPostSegment[] = []
  for (let i = 0; i < stops.length - 1; i++) {
    const start = stops[i]
    const end = stops[i + 1]
    const chunk = text.slice(start, end)
    if (!chunk) continue
    const highlight = highlights.find(
      range => range.start <= start && range.end >= end,
    )
    const mursala = mursalas.some(
      range => range.start <= start && range.end >= end,
    )
    const selected = mark && mark.start <= start && mark.end >= end
    segments.push({
      text: chunk,
      highlight: highlight?.color,
      mursala: mursala || undefined,
      selected: selected || undefined,
    })
  }
  return segments
}
